/*eslint no-constant-condition: ["error", { "checkLoops": false }]*/
import { ds } from "js/core/models/dataService";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import { BaseElement } from "js/elements/base/BaseElement";
import React, { Component } from "reactn";
import { TextStyleEnum, HorizontalAlignType, VerticalAlignType, ParagraphStyle } from "common/constants";
import { DefaultTextLayout, EvenBreakTextLayout } from "js/elements/layouts/TextLayout";
import { layoutHelper } from "js/elements/layouts/LayoutHelper";
import { SVGGroupElement } from "js/elements/base/SVGElement";
import { _, $, tinycolor } from "js/vendor";
import { serverContext } from "js/config";
import { EMAIL_VALIDATION_REGEX } from "js/react/constants";
import { blendColors } from "js/core/utilities/utilities";
import { getCSSTransform } from "js/core/utilities/geom";
import { ELEMENT_TRANSITION, SVGGroup } from "js/core/utilities/svgHelpers";
import { getValueOrDefault } from "js/core/utilities/extensions";
import perf from "js/core/utilities/perf";

const svgNS = "http://www.w3.org/2000/svg";

class TextElement extends BaseElement {
    get _canSelect() {
        return true;
    }

    get canSelect() {
        if (ds.selection.element && ds.selection.element instanceof TextElement) {
            return true;
        }
        return super.canSelect;
    }

    get _canRollover() {
        return true;
    }

    get doubleClickToSelect() {
        return false;
    }

    get passThroughSelection() {
        return false;
    }

    get minHeight() {
        return this.styles.marginTop + this.styles.marginBottom + this.styles.paddingTop + this.styles.paddingBottom + this.styles.fontSize;
    }

    get isTabbable() {
        return this.options.isTabbable === undefined ? true : this.options.isTabbable;
    }

    get singleLine() {
        return this.options.singleLine;
    }

    get allowEmptyLines() {
        return this.options.allowEmptyLines === undefined ? true : this.options.allowEmptyLines;
    }

    get allowStyling() {
        return this.options.allowStyling === undefined ? true : this.options.allowStyling;
    }

    get showStyleBar() {
        return this.options.showStyleBar === undefined ? true : this.options.showStyleBar;
    }

    get styleBarOffset() {
        return this.options.textStyleBarOffset || 6;
    }

    get allowAlignment() {
        return this.options.allowAlignment || false;
    }

    get allowedTextStyles() {
        return this.options.allowedTextStyles || [];
    }

    get textStylePropertyName() {
        return this.options.textStylePropertyName || "textStyle";
    }

    get placeholderPrompt() {
        return { text: this.options.placeholder !== undefined ? this.options.placeholder : "Type text", style: [] };
    }

    get showDefaultOverlay() {
        return !this.isTextFit;
    }

    get defaultOverlayType() {
        return "TextElementDefaultOverlay";
    }

    get allowQuickRefresh() {
        return this.options.allowQuickRefresh === undefined && !this.autoWidth ? true : this.options.allowQuickRefresh;
    }

    get scaleTextToFit() {
        return !!this.options.scaleTextToFit;
    }

    get defaultTextScale() {
        return 1;
    }

    get textScale() {
        return this._textScale;
    }

    get userFontScale() {
        if (this.matchUserScaleWithSiblings) {
            let path = this.getElementTreePath();
            let userFontScale = this.getRootElement().model.userFontScale;
            if (userFontScale && userFontScale[path]) {
                return userFontScale[path];
            } else {
                return 1;
            }
        } else {
            return this.model[this.bindTo] ? (this.model[this.bindTo].userFontScale || 1) : 1;
        }
    }

    set userFontScale(value) {
        if (this.matchUserScaleWithSiblings) {
            let path = this.getElementTreePath();
            if (!this.getRootElement().model.userFontScale) {
                this.getRootElement().model.userFontScale = {};
            }
            this.getRootElement().model.userFontScale[path] = value;
        } else {
            this.model[this.bindTo].userFontScale = value;
        }
    }

    get allowUserScale() {
        return true;
    }

    get matchUserScaleWithSiblings() {
        return true;
    }

    get minTextScale() {
        return this.options.minTextScale || 0.5;
    }

    get minFontSize() {
        return null;
    }

    get selectionPadding() {
        return 10;
    }

    get shouldCheckSpelling() {
        return this.options.spellcheck === undefined ? true : this.options.spellcheck;
    }

    get charLimit() {
        return 2000;
    }

    get paragraphStyle() {
        if (this.options.allowParagraphStyles) {
            return this.model.text_format || ParagraphStyle.PARAGRAPH;
        } else {
            return ParagraphStyle.PARAGRAPH;
        }
    }

    get bindTo() {
        return this.options.bindTo || this.id;
    }

    get textLayoutType() {
        return this.options.textLayout || "default";
    }

    get autoWidth() {
        return this.options.autoWidth || this.styles.autoWidth || false;
    }

    get autoHeight() {
        return this.options.autoHeight || this.styles.autoHeight || false;
    }

    // get constrainWidth() {
    //     return this.options.constrainWidth || false;
    // }

    get textModelValue() {
        return this.model[this.bindTo];
    }

    get canEdit() {
        if (this.model.dataSource) {
            return false;
        }

        return super.canEdit;
    }

    get firstTextLineHeight() {
        const verticalPadding = this.calculatedProps.paddedSize.height - this.calculatedProps.innerSize.height;
        const firstLineHeight = this.calculatedProps.textLayout.lines[0].fontHeight;
        return firstLineHeight + verticalPadding;
    }

    _build() {
        this.textModel = new TextModel(this.textModelValue);
        // this.textContainer = this.addElement("textContainer", () => SVGGroupElement);
    }

    updateText(text) {
        this.textModel = new TextModel({ text: text });
    }

    _migrate_5() {
        this.model[this.bindTo] = migrateTextModel(this.model[this.bindTo]);
    }

    getFontForStyle(style) {
        if (style == TextStyleEnum.BOLDITALIC) {
            if (!this.styles.emphasizedItalicFont) {
                console.warn("No emphasizedItalicFont found", this.styles);
            }
            return this.styles.emphasizedItalicFont;
        } else if (style == TextStyleEnum.BOLD) {
            if (!this.styles.emphasizedFont) {
                console.warn("No emphasizedFont found", this.styles);
            }
            return this.styles.emphasizedFont;
        } else if (style == TextStyleEnum.ITALIC) {
            if (!this.styles.italicFont) {
                console.warn("No italicFont found", this.styles);
            }
            return this.styles.italicFont;
        } else {
            return this.styles.font;
        }
    }

    _load() {
        let fonts = new Map();
        let check = (txt, style) => {
            //TODO
            // let fontName = this.getFontForStyle(style);
            let fontName = "Roboto-Regular";
            fonts.set(fontName + "." + style, fonts.get(fontName + "." + style) || []);
            fonts.get(fontName + "." + style).push(txt);
        };

        // always load ASCII and quotes “”
        check("a“”", TextStyleEnum.REGULAR);
        this.textModel.paragraphs.forEach(paragraph => paragraph.words.forEach(word => word.text.split("").map((ch, i) => check(ch, word.style || TextStyleEnum.REGULAR))));
        let results = [];
        for (let fontKey of fonts.keys()) {
            let [fontName, style] = fontKey.split(/\.(?=[^\.]+$)/); // split on last . (dot) in string, in case there are several.
            let ranges = new Set();
            for (let str of fonts.get(fontKey)) {
                for (let jj = 0; jj < str.length; jj++) {
                    ranges.add(String.fromCodePoint(str.codePointAt(jj)));
                }
            }

            for (let chr of ranges) {
                results.push(app.fontManager.loadUnicodeFont(fontName, chr, style));
            }
        }

        let promises = results.filter(res => res.then);
        if (promises.length) {
            return Promise.all(promises);
        } else {
            return Promise.resolve();
        }
    }

    _loadStyles() {
        if (this.isEditingText) {
            this.styles.filter = null;
        }

        // mjg 4/18/20 Added so we could support user-defined textAlign within single text boxes (previously only worked on TextGroups which would override the textAlign style on child TextElements)
        if (this.model.textAlign) {
            this.styles.textAlign = this.model.textAlign;
        }
    }

    _calcProps(props, options) {
        let { size, styles } = props;

        let textLayout;
        let textLayouter;

        const { dataSource } = this.model;
        if (dataSource) {
            const sourceElement = this.canvas.getElementByUniquePath(dataSource.elementId);
            const value = sourceElement.getDynamicValue(dataSource);
            this.updateText(value);
        }

        let breakType = options.breakType || this.breakType;

        switch (breakType) {
            case "even":
                textLayouter = new EvenBreakTextLayout(this);
                break;
            default:
                textLayouter = new DefaultTextLayout(this);
        }

        if (options.forceTextScale) {
            this._textScale = options.forceTextScale;
        } else {
            this._textScale = this.defaultTextScale;
        }

        let textModel = this.textModel;

        if (this.textModel.text == "" && !this.canvas.isPlayback && !window.isRenderer) {
            this.isPlaceholder = true;
            textModel = new TextModel(this.placeholderPrompt);
        } else {
            this.isPlaceholder = false;
        }

        while (true) {
            // layout the text
            textLayout = textLayouter.layout(size, textModel);

            // check if the text fits in size
            this.isTextFit = textLayout.size.height <= (size.height + 0.9) &&
                textLayout.lines.length <= (styles.maxLines || 1000) &&
                textLayout.size.width <= size.width;

            if (options.constrainWidth) {
                this.isTextFit = this.isTextFit && textLayout.size.width <= (size.width + 0.9);
            }

            // if options.scaleTextToFit is true and the text doesn't fit, keep reducing the scale until it does fit
            this._textScale -= 0.05;

            if (this.scaleTextToFit != true || this.isTextFit || this._textScale < this.minTextScale || (this.minFontSize && styles.fontSize * this._textScale < this.minFontSize)) {
                break;
            }
        }
        this._textScale += 0.05;

        let textBoxSize = textLayout.size.clone();
        let offsetX = 0;
        let offsetY = 0;

        // if options.autoWidth = true and/or options.autoHeight = true, the textelement will return the exact size dimension of the rendered text
        // and alignment will not be applied (or make sense since the width/height will match the text)
        // otherwise, the textElement will return the size that was provided and do both text and vertical align within that textbox

        let autoWidth = options.autoWidth !== undefined ? options.autoWidth : this.autoWidth;
        let autoHeight = options.autoHeight !== undefined ? options.autoHeight : this.autoHeight;

        let textAlign = options.textAlign || this.styles.textAlign || HorizontalAlignType.LEFT;
        let verticalAlign = options.verticalAlign || this.styles.verticalAlign || VerticalAlignType.TOP;

        // if (this.autoWidth != true && options.autoWidth != true) {
        if (!autoWidth) {
            //  if (!this.model.textAlign) {
            offsetX = layoutHelper.getHorizontalAlignOffset(textLayout.size.width, size.width, textAlign);
            //  }
            textBoxSize.width = size.width;
        } else {
            textBoxSize.width = Math.min(size.width, textBoxSize.width);
        }
        // if (this.autoHeight != true && options.autoHeight != true) {
        if (!autoHeight) {
            offsetY = layoutHelper.getVerticalAlignOffset(textLayout.size.height, size.height, verticalAlign);
            textBoxSize.height = size.height;
        } else {
            textBoxSize.height = Math.min(size.height, textBoxSize.height);
        }

        this.textBounds = new geom.Rect(offsetX, offsetY, textLayout.size);

        textLayout.textBounds = new geom.Rect(offsetX, offsetY, textLayout.size);

        // this.isFit = textBoxSize.width <= size.width && textBoxSize.height <= size.height;
        // console.timeEnd("textCalc");
        this.textLayouter = textLayouter;
        this.textLayout = textLayout;

        return {
            size: textBoxSize,
            isTextFit: this.isTextFit,
            textLayout,
            textBounds: textLayout.textBounds,
            textScale: this.textScale,
            autoWidth, autoHeight,
            fontSize: textLayout.lines[0].fontSize,
            textColor: options.textColor
        };
    }

    getSvgWord(index) {
        return this.renderedWords ? this.renderedWords.get(index) : null;
    }

    addSvgGroup() {
        return this.textContainer.svg.group();
    }

    get bulletSize() {
        if (this.paragraphStyle === "numbered_list") {
            return 15; // TODO: derive this value based on fontsize and digit count
        } else if (this.paragraphStyle === "bullet_list") {
            return (this.styles.bullet && this.styles.bullet.size) || 20;
        }
        return 0;
    }

    get bulletPadding() {
        if (this.paragraphStyle != "paragraph") {
            return 10;
        }
        return 0;
    }

    get offsetX() {
        return this.textBounds.left + this.styles.paddingLeft + this.bulletSize + this.bulletPadding;
    }

    get offsetY() {
        return this.textBounds.top + this.styles.paddingTop;
    }

    renderChildren(props, transition) {
        return this.drawText(props, transition);
    }

    get fontColor() {
        return this.calculatedProps.styles.fontColor || "primary_dark";
    }

    get boldFontColor() {
        return _.get(this.calculatedProps.styles, "bold.fontColor", this.calculatedProps.styles.fontColor || "primary_dark");
    }

    drawText(props, transition) {
        let { textLayout } = props;

        let slideColor = this.getSlideColor();
        let backgroundColor = this.getBackgroundColor(this);

        let regularFontColor;

        if (props.textColor) {
            regularFontColor = this.canvas.getTheme().palette.getColor(props.textColor).toRgbString();
        } else {
            regularFontColor = this.fontColor;
        }
        let boldFontColor = this.boldFontColor;

        if (this.styles.filter == "textStroke") {
            regularFontColor = blendColors(tinycolor(regularFontColor), backgroundColor).toRgbString();
        }

        // if the text is on a dark colored background and their are emphasized words, we want to fade back the color of the non-emphasized text and leave the emphasized words as white/dark
        let fadeNonEmphasizedWords = textLayout.hasEmphasizedWords && ((backgroundColor.isColor && backgroundColor.isDark()) || regularFontColor == boldFontColor);

        let offsetX = this.offsetX;
        let offsetY = this.offsetY;

        let children = [];
        let wordIndex = 0;
        let FONTSIZE = 100;

        textLayout.lines.forEach(line => {
            if (this.paragraphStyle !== "paragraph" && line.isParagraphStart) {
                let lineY = line.words.length ? line.words[0].y : line.wordY;
                switch (this.paragraphStyle) {
                    case ParagraphStyle.BULLET_LIST: {
                        let bulletSize = this.bulletSize / 2;
                        children.push(
                            <circle
                                key={`${children.length}-circle`}
                                r={bulletSize}
                                cx={this.textBounds.left + this.styles.paddingLeft + bulletSize}
                                cy={offsetY + lineY - line.height / 2 + bulletSize * 2}
                                fill={regularFontColor}
                            />
                        );
                        break;
                    }
                    case ParagraphStyle.NUMBERED_LIST: {
                        let num = (line.paragraphIndex + 1).toString();
                        children.push(
                            <text fontSize={line.fontSize}
                                key={`${children.length}-text`}
                                textAnchor="end"
                                fill={regularFontColor}
                                dx={offsetX - 10}
                                dy={offsetY + lineY}
                            >{num + "."}
                            </text>
                        );
                        break;
                    }
                }
            }

            line.words.forEach(word => {
                let font = this.textLayouter.font(word.style);
                let glyphCache = app.glyphCache[font.name];

                let x = 0;
                let glyphIndex = 0;

                let wordGlyphs = [];

                for (let i = 0; i < word.formattedText.length; i++) {
                    let char = word.formattedText[i];

                    let pathKey = char.codePointAt(0) + word.style;
                    let pathData = glyphCache[pathKey];
                    if (!pathData) {
                        let glyph = this.textLayouter.font(word.style, char).charToGlyph(char);
                        pathData = glyph.getPath(0, 0, FONTSIZE, { liga: false, rlig: false }).toPathData();
                        glyphCache[pathKey] = pathData;
                    }

                    wordGlyphs.push(React.createElement("path", {
                        key: wordGlyphs.length,
                        transform: `translate(${x} 0)`,
                        d: pathData,
                    }));
                    x += word.scaledGlyphWidths[glyphIndex];
                    glyphIndex++;
                }

                let scale = word.fontSize / FONTSIZE;

                let wordProps = {
                    key: `${children.length}${wordIndex}-word`,
                    style: {
                        transform: getCSSTransform({
                            x: offsetX + word.x,
                            y: offsetY + word.y,
                            scale: scale,
                            skew: this.isPlaceholder ? -15 : 0
                        }),
                    }
                };
                if (fadeNonEmphasizedWords && word.style.equalsAnyOf(TextStyleEnum.REGULAR, TextStyleEnum.ITALIC)) {
                    wordProps.opacity = 0.66;
                }

                if (this.isPlaceholder) {
                    wordProps.opacity = 0.5;
                }

                if (word.style.equalsAnyOf(TextStyleEnum.BOLD, TextStyleEnum.BOLDITALIC)) {
                    wordProps.fill = boldFontColor;
                } else {
                    wordProps.fill = regularFontColor;
                }

                if (transition) {
                    wordProps.style.transition = ELEMENT_TRANSITION;
                }

                if (word.link) {
                    wordProps.fill = "rgb(80, 187, 230)";
                }

                if (this.canvas.isPlayback && word.link) {
                    let link = word.link;
                    if (!link.startsWith("http") && !link.startsWith("mailto")) {
                        if (link.match(EMAIL_VALIDATION_REGEX)) {
                            link = `mailto:${link}`;
                        } else {
                            link = `https://${link}`; //always default to https
                        }
                    }

                    wordGlyphs.push(
                        <a className="canvas-text-link" href={link} target="_blank" key={wordGlyphs.length}
                            style={{
                                cursor: "pointer",
                                pointerEvents: "auto"
                            }}>
                            <rect fill="transparent"
                                x={0} y={-FONTSIZE}
                                width={_.sum(word.scaledGlyphWidths)}
                                height={FONTSIZE} />
                        </a>
                    );
                }

                children.push(React.createElement("g", wordProps, wordGlyphs));
                wordIndex++;
            });
        });

        if (!props.isTextFit && !this.canvas.isAnimating) {
            props.overflowHidden = true;
        }

        return (
            <SVGGroup {...props} key={`${this.id}-text`}>
                {children}
            </SVGGroup>
        );
    }
}

// parse pre-September 2018 html model into text and styles
function migrateTextModel(model) {
    if (typeof (model) == "object") {
        return model;
    }

    if (model == null) {
        return {
            text: "",
            styles: []
        };
    }

    let text = "";
    let styles = [];

    let html = model.toString();

    if (html == "" || html == "<p></p>") {
        return { text: "", styles: [] };
    }

    let index = 0;
    let curStyle;

    while (html.length) {
        if (_.startsWith(html, "<p>")) {
            html = html.substring(3);
        } else if (_.startsWith(html, "</p>")) {
            html = html.substring(4);
            text += String.fromCodePoint(13);
            index++;
        } else if (_.startsWith(html, "<a")) {
            curStyle = {
                bold: false,
                italic: false,
                link: html.substring(html.indexOf('"') + 1, html.indexOf('"', html.indexOf('"') + 1)),
                start: index,
                end: index
            };
            html = html.substring(html.indexOf('"', html.indexOf('"') + 1) + 2);
        } else if (_.startsWith(html, "</a>")) {
            if (curStyle) {
                curStyle.end = index;
                styles.push(curStyle);
            }
            html = html.substring(4);
        } else if (_.startsWith(html, "<b>")) {
            curStyle = {
                bold: true,
                italic: false,
                link: null,
                start: index,
                end: index
            };
            html = html.substring(3);
        } else if (_.startsWith(html, "</b>")) {
            if (curStyle) {
                curStyle.end = index;
                styles.push(curStyle);
            }
            html = html.substring(4);
        } else {
            text += html[0];
            html = html.substring(1);
            index++;
        }
    }

    // strip trailing newline
    if (text.codePointAt(text.length - 1) == 13) {
        text = text.substring(0, text.length - 1);
    }

    return { text, styles };
}

class TextModel {
    static get breakWordRegex() {
        return new RegExp(/([\s!"#$%&()*+,.:;<=>?@\[\]^_-`{|}~“”])/);
    }

    static get punctuationRegex() {
        return new RegExp(/([!"#$%&()*+,.:;<=>?@\[\]^_-`{|}~“”])/);
    }

    constructor(model) {
        if (model == null || typeof (model) != "object") {
            if (model == undefined) {
                model = { text: "" };
            } else {
                model = { text: model };
            }
        }
        if (model.text == null) {
            model.text = "";
        }
        this.setText(model.text.toString(), model.styles);
    }

    getStyleType(style) {
        if (style.bold && style.italic) {
            return TextStyleEnum.BOLDITALIC;
        } else if (style.bold) {
            return TextStyleEnum.BOLD;
        } else if (style.italic) {
            return TextStyleEnum.ITALIC;
        } else {
            return TextStyleEnum.REGULAR;
        }
    }

    setText(text = "", styles) {
        this.text = text.toString();
        this.styles = styles;
        this.links = [];

        // generate the array of paragraph and words used by the textLayouter
        this.paragraphs = [];
        let charIndex = 0;

        let hasOpenQuote = null;

        let paragraphs = this.text.split(String.fromCodePoint(13));
        paragraphs.forEach((p, i) => {
            let paragraph = { words: [] };
            for (let w of p.split(TextModel.breakWordRegex)) {
                if (w != "") {
                    let style = _.find(this.styles, s => charIndex >= s.start && charIndex < s.end);
                    if (!style) {
                        style = { bold: false, italic: false, link: null };
                    }

                    if (["“", '"'].find(c => c === w)) {
                        if (hasOpenQuote === null) {
                            hasOpenQuote = w;
                            w = "“";
                        } else {
                            w = "”";
                            hasOpenQuote = null;
                        }
                    }

                    paragraph.words.push({
                        text: w,
                        style: this.getStyleType(style),
                        link: style.link
                    });
                    charIndex += w.length;
                }
            }
            if (paragraphs.length - 1 > i) {
                // if there are more paragraphs, add a newline char at end of paragraph
                paragraph.words.push({
                    text: String.fromCodePoint(13),
                    style: TextStyleEnum.REGULAR,
                    link: null
                });
                charIndex += 1;
            }
            this.paragraphs.push(paragraph);
        });
    }
}

export { TextElement, TextModel, migrateTextModel };

export const elements = {
    TextElement
};
